import React, { useEffect } from "react";
import { toast } from "sonner";
import { useStaffAccess } from "@/hooks/useStaffAccess";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import ChildrenPage from "@/pages/ChildrenPage";
import AttendancePage from "@/pages/AttendancePage";
import StaffPage from "@/pages/StaffPage";
import StaffSchedulePage from "@/pages/StaffSchedulePage";
import RatioWorksheetPage from "@/pages/RatioWorksheetPage";
import CompliancePage from "@/pages/CompliancePage";
import SafetyDrillsPage from "@/pages/SafetyDrillsPage";
import ContinuingEducationPage from "@/pages/ContinuingEducationPage";
import BillingPage from "@/pages/BillingPage";
import MessagesPage from "@/pages/MessagesPage";
import DailyReportsPage from "@/pages/DailyReportsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import SettingsPage from "@/pages/SettingsPage";
import EmployeeApplicationPage from "@/pages/EmployeeApplicationPage";
import ChildApplicationPage from "@/pages/ChildApplicationPage";
import WaitlistPage from "@/pages/WaitlistPage";
import PricingPage from "@/pages/PricingPage";
import TransportationPage from "@/pages/TransportationPage";
import DownloadPage from "@/pages/DownloadPage";
import AppAdoptionPage from "@/pages/AppAdoptionPage";
import { AppInstallTracker } from "@/components/AppInstallTracker";
import TermsPage from "@/pages/TermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import AccountSetupPage from "@/pages/AccountSetupPage";
import IncidentReportsPage from "@/pages/IncidentReportsPage";
import TimeClockPage from "@/pages/TimeClockPage";
import TimeClockAdminPage from "@/pages/TimeClockAdminPage";
import MyHoursPage from "@/pages/MyHoursPage";
import LearningPage from "@/pages/LearningPage";
import MenuPage from "@/pages/MenuPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import SubscriptionGatePage from "@/pages/SubscriptionGatePage";
import ParentsPage from "@/pages/ParentsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ParentEnrollmentPage from "@/pages/ParentEnrollmentPage";
import ParentCheckinPage from "@/pages/ParentCheckinPage";
import ParentSignupPage from "@/pages/ParentSignupPage";
import DataMigrationPage from "@/pages/DataMigrationPage";
import AccountingPage from "@/pages/AccountingPage";
import NewslettersPage from "@/pages/NewslettersPage";
import DriverDashboard from "@/pages/DriverDashboard";
import DriverPinLogin from "@/pages/DriverPinLogin";
import DriversPage from "@/pages/DriversPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import ConnectivityBanner from "@/components/ConnectivityBanner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { SubscriptionProvider, useSubscription } from "@/context/SubscriptionContext";

const TeacherPageGuard = ({ children }) => {
  const access = useStaffAccess();
  const { pathname } = useLocation();
  const pages = access?.pages;
  const blocked = pages && !['/profile', ...pages].some((p) => pathname === p || pathname.startsWith(`${p}/`));
  useEffect(() => { if (blocked) toast.info('Ask your director for access to this page'); }, [blocked]);
  if (!access) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  return blocked ? <Navigate to="/provider" replace /> : children;
};

const ProtectedRoute = ({ children, allowedRoles, requireSubscription = true }) => {
  const { user, loading } = useAuth();
  const { hasAccess, loading: subLoading, requiresSubscription } = useSubscription();
  
  if (loading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "provider") return <Navigate to="/provider" replace />;
    if (user.role === "parent") return <Navigate to="/parent" replace />;
    if (user.role === "driver") return <Navigate to="/driver" replace />;
  }
  
  // Check subscription for admin/provider roles
  if (requireSubscription && requiresSubscription && !hasAccess) {
    return <Navigate to="/subscribe" replace />;
  }
  
  return user.role === "provider" ? <TeacherPageGuard>{children}</TeacherPageGuard> : children;
};

const DashboardRedirect = () => {
  const { user } = useAuth();
  const { hasAccess, requiresSubscription } = useSubscription();
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Check subscription for admin/provider
  if (requiresSubscription && !hasAccess) {
    return <Navigate to="/subscribe" replace />;
  }
  
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "provider") return <Navigate to="/provider" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  
  return <Navigate to="/" replace />;
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <PermissionsProvider>
          <div className="App min-h-screen bg-background">
            <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/driver-pin" element={<DriverPinLogin />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Subscription Gate */}
              <Route path="/subscribe" element={<SubscriptionGatePage />} />
              
              {/* Dashboard Redirect */}
              <Route path="/dashboard" element={<DashboardRedirect />} />
            
            {/* Admin Routes */}
            <Route path="/app-adoption" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppAdoptionPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            {/* Provider Routes */}
            <Route path="/provider" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <ProviderDashboard />
              </ProtectedRoute>
            } />
            
            {/* Parent Routes */}
            <Route path="/parent" element={
              <ProtectedRoute allowedRoles={["parent"]}>
                <ParentDashboard />
              </ProtectedRoute>
            } />
            
            {/* Driver Routes */}
            <Route path="/driver" element={
              <ProtectedRoute allowedRoles={["driver"]} requireSubscription={false}>
                <DriverDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/drivers" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DriversPage />
              </ProtectedRoute>
            } />
            
            {/* Shared Protected Routes */}
            <Route path="/children" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]}>
                <ChildrenPage />
              </ProtectedRoute>
            } />
            
            <Route path="/attendance" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <AttendancePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <StaffPage />
              </ProtectedRoute>
            } />
            
            <Route path="/parents" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <ParentsPage />
              </ProtectedRoute>
            } />

            <Route path="/parent/checkin" element={
              <ProtectedRoute allowedRoles={["parent"]}>
                <ParentCheckinPage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/schedule" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <StaffSchedulePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/ratio-worksheet" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <RatioWorksheetPage />
              </ProtectedRoute>
            } />
            
            <Route path="/compliance" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <CompliancePage />
              </ProtectedRoute>
            } />

            <Route path="/safety-drills" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <SafetyDrillsPage />
              </ProtectedRoute>
            } />

            <Route path="/continuing-education" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <ContinuingEducationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/billing" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]}>
                <BillingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/accounting" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <AccountingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/messages" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent", "driver"]}>
                <MessagesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/daily-reports" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]}>
                <DailyReportsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/incident-reports" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]}>
                <IncidentReportsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/payment-success" element={
              <ProtectedRoute allowedRoles={["parent"]}>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <SettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/data-migration" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DataMigrationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/newsletters" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <NewslettersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/waitlist" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <WaitlistPage />
              </ProtectedRoute>
            } />
            
            <Route path="/timeclock" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "driver"]}>
                <TimeClockPage />
              </ProtectedRoute>
            } />
            
            <Route path="/timeclock/admin" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <TimeClockAdminPage />
              </ProtectedRoute>
            } />
            
            <Route path="/my-hours" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <MyHoursPage />
              </ProtectedRoute>
            } />
            
            <Route path="/learning" element={
              <ProtectedRoute allowedRoles={["admin", "provider"]}>
                <LearningPage />
              </ProtectedRoute>
            } />
            
            <Route path="/menu" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]}>
                <MenuPage />
              </ProtectedRoute>
            } />
            
            {/* Public Routes */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/transportation" element={<TransportationPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/account-setup" element={<AccountSetupPage />} />
            <Route path="/subscription/success" element={
              <ProtectedRoute allowedRoles={["admin", "provider", "parent"]} requireSubscription={false}>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />
            
            {/* Public Application Routes */}
            <Route path="/apply/employee" element={<EmployeeApplicationPage />} />
            <Route path="/apply/child" element={<ChildApplicationPage />} />
            <Route path="/enroll/:token" element={<ParentEnrollmentPage />} />
            <Route path="/parent-signup/:token" element={<ParentSignupPage />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
        <AppInstallTracker />
        <ConnectivityBanner />
      </div>
        </PermissionsProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
